const bulkAddEventListener = function (object, events, callback) {
  events.forEach(event => object.addEventListener(event, callback))
}

const bulkRemoveEventListener = function (object, events, callback) {
  events.forEach(event => object.removeEventListener(event, callback))
}

/**
 * Tracks if the user is interacting with the page and if the page is visible
 *
 * @example
 * import { Idle } from 'tmr/services/idle'
 * const idle = new Idle({ idle: 30000, onIdle: () => {
 *   console.log('idle')
 * } })
 * idle.start()
 *
 * // also available
 * idle.idle
 * idle.visible
 */
export class Idle {
  constructor(options = {}) {
    this.defaults = {
      idle: 60000, // idle time in ms
      events: ['mousemove', 'keydown', 'mousedown', 'touchstart'], // events that will trigger the idle resetter
      onIdle: () => {}, // callback function to be executed after idle time
      onActive: () => {}, // callback function to be executed after back form idleness
      onHide: () => {}, // callback function to be executed when window become hidden
      onShow: () => {}, // callback function to be executed when window become visible
      keepTracking: true, // set it to false of you want to track only once
      startAtIdle: false, // set it to true if you want to start in the idle state
      recurIdleCall: false,
    }
    this.settings = Object.assign({}, this.defaults, options)
    this.visibilityEvents = [
      'visibilitychange',
      'webkitvisibilitychange',
      'mozvisibilitychange',
      'msvisibilitychange',
    ]
    this.clearTimeout = null

    this.reset()

    this.stopListener = () => this.stop()

    this.idlenessEventsHandler = () => {
      if (this.idle) {
        this.idle = false
        this.settings.onActive.call()
      }
      this.resetTimeout()
    }

    this.visibilityEventsHandler = () => {
      if (
        document.hidden ||
        document.webkitHidden ||
        document.mozHidden ||
        document.msHidden
      ) {
        if (this.visible) {
          this.visible = false
          this.settings.onHide.call()
        }
      } else {
        if (!this.visible) {
          this.visible = true
          this.settings.onShow.call()
        }
      }
    }
  }

  resetTimeout(keepTracking = this.settings.keepTracking) {
    if (this.clearTimeout) {
      this.clearTimeout()
      this.clearTimeout = null
    }
    if (keepTracking) {
      this.timeout(this.settings)
    }
  }

  timeout() {
    let timer = this.settings.recurIdleCall
      ? {
          set: setInterval.bind(window),
          clear: clearInterval.bind(window),
        }
      : {
          set: setTimeout.bind(window),
          clear: clearTimeout.bind(window),
        }

    let id = timer.set(() => {
      this.idle = true
      this.settings.onIdle.call()
    }, this.settings.idle)

    this.clearTimeout = () => timer.clear(id)
  }

  start() {
    window.addEventListener('idle:stop', this.stopListener)
    this.timeout(this.settings)

    bulkAddEventListener(
      window,
      this.settings.events,
      this.idlenessEventsHandler
    )

    if (this.settings.onShow || this.settings.onHide) {
      bulkAddEventListener(
        document,
        this.visibilityEvents,
        this.visibilityEventsHandler
      )
    }

    return this
  }

  stop() {
    window.removeEventListener('idle:stop', this.stopListener)

    bulkRemoveEventListener(
      window,
      this.settings.events,
      this.idlenessEventsHandler
    )
    this.resetTimeout(false)

    if (this.settings.onShow || this.settings.onHide) {
      bulkRemoveEventListener(
        document,
        this.visibilityEvents,
        this.visibilityEventsHandler
      )
    }

    return this
  }

  reset({
    idle = this.settings.startAtIdle,
    visible = !this.settings.startAtIdle,
  } = {}) {
    this.idle = idle
    this.visible = visible
    return this
  }

  set(options) {
    this.settings = Object.assign(this.settings, options)
  }
}
